import React, { useState } from 'react';
import {
  Settings,
  Building2,
  CreditCard,
  Database,
  Bell,
  Save,
  CheckCircle2,
  Shield,
  Key,
  ChevronDown,
  ChevronUp,
  FileText,
  Users,
  Eye,
  Sliders,
  Globe2,
  UserCheck,
  MessageSquare,
  Package,
  MessagesSquare,
  Receipt,
  LayoutGrid,
  Lock,
  Plus,
  Trash2,
  QrCode,
  Smartphone,
  Printer,
  Sparkles,
  AlertCircle,
  HelpCircle,
  Camera,
  Car,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  ExternalLink,
  ArrowLeft,
} from 'lucide-react';
import { AdminUserPermissionsView } from './settings/AdminUserPermissionsView';
import { AutoReplySettingsView } from './settings/AutoReplySettingsView';
import { ParcelSettingsView } from './settings/ParcelSettingsView';
import { ChatSettingsView } from './settings/ChatSettingsView';
import { ProjectInfoSettingsView } from './settings/ProjectInfoSettingsView';
import { BoardMembersSettingsView } from './settings/BoardMembersSettingsView';
import { ForeignQuotaDocSettingsView } from './settings/ForeignQuotaDocSettingsView';
import { ForeignQuotaNotificationSettingsView } from './settings/ForeignQuotaNotificationSettingsView';

interface Props {
  onOpenSupabaseModal: () => void;
  isSupabaseConnected: boolean;
}

export type SettingKey =
  | 'default_accounting'
  | 'assets'
  | 'project_info'
  | 'documents'
  | 'board_members'
  | 'security_visitors'
  | 'developer'
  | 'resident_doc_visibility'
  | 'menu_customization'
  | 'foreign_quota'
  | 'foreign_quota_doc'
  | 'foreign_quota_notification'
  | 'user_permissions'
  | 'auto_reply'
  | 'parcels'
  | 'chat_settings'
  | 'invoice_template'
  | 'admin_menu';

export const AdminSettingsView: React.FC<Props> = ({
  onOpenSupabaseModal,
  isSupabaseConnected,
}) => {
  // State for which setting panel is open / expanded (null = list view)
  const [activeSetting, setActiveSetting] = useState<SettingKey | null>(null);
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [expandedDropdowns, setExpandedDropdowns] = useState<Record<string, boolean>>({
    foreign_quota: true,
  });

  const triggerSave = (label: string) => {
    setSaveToast(`บันทึก "${label}" เรียบร้อยแล้ว`);
    setTimeout(() => setSaveToast(null), 3000);
  };


  // 1. ตั้งค่าบัญชีเริ่มต้น
  const [bankName, setBankName] = useState('ธนาคารกสิกรไทย (KBANK)');
  const [bankAccount, setBankAccount] = useState('142-8-99214-5');
  const [accountName, setAccountName] = useState('นิติบุคคล มบ.นราวดี อิลิแกนซ์');
  const [ratePerSqw, setRatePerSqw] = useState(35);
  const [dueDay, setDueDay] = useState(5);
  const [lateFeePercent, setLateFeePercent] = useState(1.5);
  const [accountingPeriod, setAccountingPeriod] = useState('รายเดือน (Monthly)');
  const [withholdingTaxRate, setWithholdingTaxRate] = useState(1.0);

  // 2. ตั้งค่าสินทรัพย์
  const [assetDepreciationMethod, setAssetDepreciationMethod] = useState('เส้นตรง (Straight Line 5 ปี)');
  const [assetBudgetLimit, setAssetBudgetLimit] = useState(30000);
  const [autoQrAssetTag, setAutoQrAssetTag] = useState(true);

  // 3. ตั้งค่าข้อมูลโครงการ
  const [juristicName, setJuristicName] = useState('นิติบุคคลหมู่บ้านจัดสรร นราวดี อิลิแกนซ์');
  const [juristicRegNo, setJuristicRegNo] = useState('อย. 14/2565');
  const [taxId, setTaxId] = useState('0-1455-68001-42-1');
  const [phone, setPhone] = useState('035-998-112');
  const [emergencyPhone, setEmergencyPhone] = useState('089-999-8888');
  const [email, setEmail] = useState('juristic@narawadee-elegance.com');
  const [totalUnits, setTotalUnits] = useState(128);
  const [totalLandSqw, setTotalLandSqw] = useState(12540);

  // 4. ตั้งค่าเอกสาร
  const [invoicePrefix, setInvoicePrefix] = useState('INV-2026-');
  const [receiptPrefix, setReceiptPrefix] = useState('REC-2026-');
  const [debtPrefix, setDebtPrefix] = useState('CLR-2026-');
  const [docHeaderNotes, setDocHeaderNotes] = useState('นิติบุคคล มบ.นราวดี อิลิแกนซ์ (โครงการอยุธยา)');
  const [digitalStampEnabled, setDigitalStampEnabled] = useState(true);

  // 5. ตั้งค่าคณะกรรมการ
  const [boardTermYears, setBoardTermYears] = useState(2);
  const [boardCurrentTerm, setBoardCurrentTerm] = useState('วาระปี 2567 - 2569');
  const [boardApprovalLimit, setBoardApprovalLimit] = useState(50000);
  const [quorumRatio, setQuorumRatio] = useState('กึ่งหนึ่งของกรรมการทั้งหมด (5/7 ท่าน)');

  // 6. ตั้งค่าระบบรักษาความปลอดภัยและผู้มาติดต่อ
  const [visitorPassType, setVisitorPassType] = useState('สแกน QR Code + บันทึกบัตรประชาชน');
  const [overnightParkingFee, setOvernightParkingFee] = useState(100);
  const [lprBarrierAutoOpen, setLprBarrierAutoOpen] = useState(true);
  const [patrolPointsCount, setPatrolPointsCount] = useState(7);
  const [visitorMaxStayHours, setVisitorMaxStayHours] = useState(24);

  // 7. ตั้งค่าสำหรับผู้พัฒนาโครงการ
  const [developerName, setDeveloperName] = useState('บริษัท นราวดี พร็อพเพอร์ตี้ จำกัด');
  const [developerContact, setDeveloperContact] = useState('02-998-8877 ต่อฝ่ายบริการลูกค้า');
  const [warrantyYears, setWarrantyYears] = useState(5);
  const [sinkingFundAmount, setSinkingFundAmount] = useState(2500000);

  // 8. ตั้งค่าการมองเห็นเอกสารในฝั่งบัญชีแยกตามสถานะ (แอปปลูกบ้าน)
  const [allowResidentViewFinancialReport, setAllowResidentViewFinancialReport] = useState(true);
  const [allowResidentViewAgmMinutes, setAllowResidentViewAgmMinutes] = useState(true);
  const [residentReceiptHistoryMonths, setResidentReceiptHistoryMonths] = useState(12);
  const [pdpaMaskingEnabled, setPdpaMaskingEnabled] = useState(true);

  // 9. ตั้งค่าปรับแต่งเมนู
  const [showHotlineMenu, setShowHotlineMenu] = useState(true);
  const [showFacilityBooking, setShowFacilityBooking] = useState(true);
  const [showDefectReport, setShowDefectReport] = useState(true);
  const [showEVotingMenu, setShowEVotingMenu] = useState(true);
  const [primaryThemeColor, setPrimaryThemeColor] = useState('น้ำเงินสุขุม (Classic Navy Blue)');

  // 10. ตั้งค่ากรรมสิทธิ์ชาวต่างชาติ
  const [foreignQuotaMaxPercent, setForeignQuotaMaxPercent] = useState(49);
  const [foreignCurrentPercent, setForeignCurrentPercent] = useState(6.25);
  const [requireFetForm, setRequireFetForm] = useState(true);
  const [bilingualDocs, setBilingualDocs] = useState(true);

  // 11. ตั้งค่าสิทธิ์ผู้ใช้งาน
  const [twoFactorAuth, setTwoFactorAuth] = useState(false);
  const [sessionTimeoutMins, setSessionTimeoutMins] = useState(60);
  const [auditLogRetentionDays, setAuditLogRetentionDays] = useState(90);

  // 12. ตั้งค่าการตอบกลับข้อความ
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(true);
  const [autoReplyMessage, setAutoReplyMessage] = useState(
    'นิติบุคคลได้รับเรื่องของท่านแล้ว เจ้าหน้าที่จะติดต่อกลับภายในเวลาทำการ (08:30 - 17:30 น.)'
  );
  const [lineNotifyActive, setLineNotifyActive] = useState(true);
  const [officeHours, setOfficeHours] = useState('จันทร์ - เสาร์ (08:30 - 17:30 น.)');

  // 13. ตั้งค่าพัสดุ
  const [parcelShelfZones, setParcelShelfZones] = useState('โซน A, B, C, D (รวม 40 ช่องวาง)');
  const [parcelMaxKeepDays, setParcelMaxKeepDays] = useState(7);
  const [parcelSmsAlert, setParcelSmsAlert] = useState(true);
  const [parcelRequireSign, setParcelRequireSign] = useState(true);

  // 14. ตั้งค่าแชท
  const [chatEnabled, setChatEnabled] = useState(true);
  const [chatBotFaqEnabled, setChatBotFaqEnabled] = useState(true);
  const [chatMediaAllow, setChatMediaAllow] = useState(true);

  // 15. ตั้งค่ารูปแบบใบแจ้งหนี้
  const [invoiceTemplateStyle, setInvoiceTemplateStyle] = useState('แบบทางการ (Official A4 with PromptPay QR)');
  const [invoiceFooterNote, setInvoiceFooterNote] = useState(
    'โปรดชำระภายในวันที่ 5 ของทุกเดือน ขอบพระคุณที่ร่วมสร้างสรรค์สังคมน่าอยู่'
  );
  const [includeWatermark, setIncludeWatermark] = useState(true);

  // 16. ตั้งค่าเมนูแอดมิน
  const [compactSidebarByDefault, setCompactSidebarByDefault] = useState(false);
  const [enableKeyboardShortcuts, setEnableKeyboardShortcuts] = useState(true);
  const [showQuickStatWidgets, setShowQuickStatWidgets] = useState(true);

  // Definition of the 16 menu cards matching the user's uploaded mockup
  const leftMenuItems: Array<{ key: SettingKey; title: string; hasDropdown: boolean }> = [
    { key: 'default_accounting', title: 'ตั้งค่าบัญชีเริ่มต้น', hasDropdown: true },
    { key: 'assets', title: 'ตั้งค่าสินทรัพย์', hasDropdown: false },
    { key: 'project_info', title: 'ตั้งค่าข้อมูลโครงการ', hasDropdown: false },
    { key: 'documents', title: 'ตั้งค่าเอกสาร', hasDropdown: true },
    { key: 'board_members', title: 'ตั้งค่าคณะกรรมการ', hasDropdown: false },
    { key: 'security_visitors', title: 'ตั้งค่าระบบรักษาความปลอดภัยและผู้มาติดต่อ', hasDropdown: true },
    { key: 'developer', title: 'ตั้งค่าสำหรับผู้พัฒนาโครงการ', hasDropdown: false },
    { key: 'resident_doc_visibility', title: 'ตั้งค่าการมองเห็นเอกสารในฝั่งบัญชีแยกตามสถานะ (แอปปลูกบ้าน)', hasDropdown: false },
  ];

  const rightMenuItems: Array<{ key: SettingKey; title: string; hasDropdown: boolean }> = [
    { key: 'menu_customization', title: 'ตั้งค่าปรับแต่งเมนู', hasDropdown: false },
    { key: 'foreign_quota', title: 'ตั้งค่ากรรมสิทธิ์ชาวต่างชาติ', hasDropdown: true },
    { key: 'user_permissions', title: 'ตั้งค่าสิทธิ์ผู้ใช้งาน', hasDropdown: false },
    { key: 'auto_reply', title: 'ตั้งค่าการตอบกลับข้อความ', hasDropdown: false },
    { key: 'parcels', title: 'ตั้งค่าพัสดุ', hasDropdown: false },
    { key: 'chat_settings', title: 'ตั้งค่าแชท', hasDropdown: false },
    { key: 'invoice_template', title: 'ตั้งค่ารูปแบบใบแจ้งหนี้', hasDropdown: false },
    { key: 'admin_menu', title: 'ตั้งค่าเมนูแอดมิน', hasDropdown: false },
  ];

  const handleCardClick = (key: SettingKey) => {
    setActiveSetting(key);
  };

  // If dedicated sub-view is selected, render it directly
  if (activeSetting === 'user_permissions') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <AdminUserPermissionsView onBack={() => setActiveSetting(null)} />
      </div>
    );
  }

  if (activeSetting === 'auto_reply') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <AutoReplySettingsView
          onBack={() => setActiveSetting(null)}
          onSave={() => triggerSave('ตั้งค่าการตอบกลับข้อความ')}
        />
      </div>
    );
  }

  if (activeSetting === 'parcels') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <ParcelSettingsView
          onBack={() => setActiveSetting(null)}
          onSave={() => triggerSave('ตั้งค่าพัสดุ')}
        />
      </div>
    );
  }

  if (activeSetting === 'chat_settings') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <ChatSettingsView
          onBack={() => setActiveSetting(null)}
          onSave={() => triggerSave('ตั้งค่าแชท')}
        />
      </div>
    );
  }

  if (activeSetting === 'project_info') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <ProjectInfoSettingsView
          onBack={() => setActiveSetting(null)}
          onSave={() => triggerSave('ตั้งค่าข้อมูลโครงการ')}
        />
      </div>
    );
  }

  if (activeSetting === 'board_members') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <BoardMembersSettingsView
          onBack={() => setActiveSetting(null)}
          onSave={() => triggerSave('ตั้งค่าคณะกรรมการ')}
        />
      </div>
    );
  }

  if (activeSetting === 'foreign_quota' || activeSetting === 'foreign_quota_doc') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <ForeignQuotaDocSettingsView
          onBack={() => setActiveSetting(null)}
          onSave={() => triggerSave('ตั้งค่าเอกสารกรรมสิทธิ์ชาวต่างชาติ')}
        />
      </div>
    );
  }

  if (activeSetting === 'foreign_quota_notification') {
    return (
      <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
        {saveToast && (
          <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200" />
            <span>{saveToast}</span>
          </div>
        )}
        <ForeignQuotaNotificationSettingsView
          onBack={() => setActiveSetting(null)}
          onSave={() => triggerSave('ตั้งค่าการแจ้งเตือนกรรมสิทธิ์ชาวต่างชาติ')}
        />
      </div>
    );
  }

  // Get active menu title for breadcrumbs/header
  const allMenuItems = [...leftMenuItems, ...rightMenuItems];
  const activeMenuItem = allMenuItems.find((m) => m.key === activeSetting);

  return (
    <div id="admin-settings-container" className="space-y-6 max-w-5xl mx-auto font-prompt">
      {/* Toast Notification */}
      {saveToast && (
        <div className="fixed top-5 right-5 z-50 p-4 bg-emerald-700 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold">
          <CheckCircle2 className="w-5 h-5 text-emerald-200" />
          <span>{saveToast}</span>
        </div>
      )}

      {/* ===================== LIST VIEW: 16 MENU CARDS ===================== */}
      {activeSetting === null ? (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* Main Settings Header */}
          <div className="text-center space-y-1.5 py-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">ตั้งค่า</h1>
            <p className="text-xs sm:text-sm text-slate-500 max-w-xl mx-auto">
              จัดการและกำหนดค่าระบบนิติบุคคล ข้อมูลโครงการ บัญชีการเงิน สิทธิการเข้าถึง และการบริการสมาชิก
            </p>
          </div>

          {/* 2-Column Grid Layout matching user's reference mockup */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-4">
            {/* Left Column (8 items) */}
            <div className="space-y-3">
              {leftMenuItems.map((item) => (
                <div key={item.key} className="transition-all duration-200">
                  <button
                    type="button"
                    onClick={() => handleCardClick(item.key)}
                    className="w-full p-4 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer bg-white border-slate-200/90 hover:border-blue-400 hover:bg-blue-50/30 hover:shadow-sm shadow-2xs group"
                  >
                    <div className="flex items-center gap-3 min-w-0 pr-2">
                      <div className="p-1.5 rounded-lg border shrink-0 bg-slate-50 text-slate-400 border-slate-200 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors">
                        <Settings className="w-4 h-4" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold truncate text-slate-700 group-hover:text-blue-900">
                        {item.title}
                      </span>
                    </div>

                    <div className="shrink-0 text-slate-400 group-hover:text-blue-600">
                      {item.hasDropdown ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4 -rotate-90" />
                      )}
                    </div>
                  </button>
                </div>
              ))}
            </div>

            {/* Right Column (8 items) */}
            <div className="space-y-3">
              {rightMenuItems.map((item) => {
                if (item.key === 'foreign_quota') {
                  const isExpanded = expandedDropdowns['foreign_quota'] ?? false;
                  return (
                    <div
                      key={item.key}
                      className="rounded-xl border border-slate-200/90 bg-white overflow-hidden shadow-2xs transition-all"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedDropdowns((prev) => ({
                            ...prev,
                            foreign_quota: !prev['foreign_quota'],
                          }))
                        }
                        className="w-full p-4 text-left flex items-center justify-between transition-all cursor-pointer hover:bg-slate-50/70 group"
                      >
                        <div className="flex items-center gap-3 min-w-0 pr-2">
                          <div className="p-1.5 rounded-lg border shrink-0 bg-slate-50 text-slate-400 border-slate-200 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors">
                            <Settings className="w-4 h-4" />
                          </div>
                          <span className="text-xs sm:text-sm font-semibold truncate text-slate-700 group-hover:text-blue-900">
                            {item.title}
                          </span>
                        </div>

                        <div className="shrink-0 text-slate-400 group-hover:text-blue-600">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4 -rotate-90" />
                          )}
                        </div>
                      </button>

                      {/* Dropdown Sub-menu Items matching Image 8 & 12 */}
                      {isExpanded && (
                        <div className="border-t border-slate-100 bg-slate-50/50 py-1.5 px-3 space-y-1">
                          <button
                            type="button"
                            onClick={() => handleCardClick('foreign_quota_doc')}
                            className="w-full py-2 px-3 rounded-lg text-left text-xs font-medium text-slate-700 hover:text-blue-700 hover:bg-blue-50/60 flex items-center justify-between transition-colors cursor-pointer group"
                          >
                            <span className="truncate">ตั้งค่าเอกสารกรรมสิทธิ์ชาวต่างชาติ</span>
                            <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-slate-400 group-hover:text-blue-600" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleCardClick('foreign_quota_notification')}
                            className="w-full py-2 px-3 rounded-lg text-left text-xs font-medium text-slate-700 hover:text-blue-700 hover:bg-blue-50/60 flex items-center justify-between transition-colors cursor-pointer group"
                          >
                            <span className="truncate">ตั้งค่าการแจ้งเตือน</span>
                            <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-slate-400 group-hover:text-blue-600" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={item.key} className="transition-all duration-200">
                    <button
                      type="button"
                      onClick={() => handleCardClick(item.key)}
                      className="w-full p-4 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer bg-white border-slate-200/90 hover:border-blue-400 hover:bg-blue-50/30 hover:shadow-sm shadow-2xs group"
                    >
                      <div className="flex items-center gap-3 min-w-0 pr-2">
                        <div className="p-1.5 rounded-lg border shrink-0 bg-slate-50 text-slate-400 border-slate-200 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors">
                          <Settings className="w-4 h-4" />
                        </div>
                        <span className="text-xs sm:text-sm font-semibold truncate text-slate-700 group-hover:text-blue-900">
                          {item.title}
                        </span>
                      </div>

                      <div className="shrink-0 text-slate-400 group-hover:text-blue-600">
                        {item.hasDropdown ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4 -rotate-90" />
                        )}
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* ===================== DETAIL VIEW: SUB-MENU SETTING WITH BACK BUTTON ===================== */
        <div className="space-y-4 animate-in fade-in duration-200">
          {/* Top Bar with Back Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setActiveSetting(null)}
                className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer"
                title="ย้อนกลับไปเมนูตั้งค่า"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
                  {activeMenuItem?.title || 'รายละเอียดการตั้งค่า'}
                </h1>
                <p className="text-xs text-slate-500">
                  ตั้งค่า / {activeMenuItem?.title}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setActiveSetting(null)}
              className="px-3.5 py-1.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-xs font-semibold text-slate-600 flex items-center gap-1.5 transition-colors cursor-pointer self-start sm:self-auto"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>กลับไปเมนูตั้งค่า</span>
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-5 sm:p-7 space-y-6">
          {/* ================= 1. ตั้งค่าบัญชีเริ่มต้น ================= */}
          {activeSetting === 'default_accounting' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-blue-50 text-blue-700 rounded-xl border border-blue-200">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">1. ตั้งค่าบัญชีเริ่มต้น (Default Accounting)</h2>
                    <p className="text-xs text-slate-500">บัญชีธนาคารรับเงิน, อัตราค่าส่วนกลาง, รอบบิล และเบี้ยปรับ</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าบัญชีเริ่มต้น')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ธนาคารหลักสำหรับรับชำระ</label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เลขที่บัญชี</label>
                  <input
                    type="text"
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-blue-700"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ชื่อบัญชีรับเงิน</label>
                  <input
                    type="text"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">รอบการออกบิล</label>
                  <select
                    value={accountingPeriod}
                    onChange={(e) => setAccountingPeriod(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="รายเดือน (Monthly)">รายเดือน (Monthly)</option>
                    <option value="ราย 3 เดือน (Quarterly)">ราย 3 เดือน (Quarterly)</option>
                    <option value="ราย 6 เดือน (Bi-annual)">ราย 6 เดือน (Bi-annual)</option>
                    <option value="รายปี (Annual)">รายปี (Annual)</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">อัตราค่าส่วนกลาง (บาท / ตร.ว. / เดือน)</label>
                  <input
                    type="number"
                    value={ratePerSqw}
                    onChange={(e) => setRatePerSqw(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-blue-700"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">วันครบกำหนดชำระของทุกเดือน (Due Date)</label>
                  <input
                    type="number"
                    min="1"
                    max="28"
                    value={dueDay}
                    onChange={(e) => setDueDay(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">อัตราเบี้ยปรับล่าช้า (% ต่อเดือนตามข้อบังคับ)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={lateFeePercent}
                    onChange={(e) => setLateFeePercent(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-rose-600 font-bold"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">อัตราภาษีหัก ณ ที่จ่าย ค่าจ้างบริการ (%)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={withholdingTaxRate}
                    onChange={(e) => setWithholdingTaxRate(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 2. ตั้งค่าสินทรัพย์ ================= */}
          {activeSetting === 'assets' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-indigo-50 text-indigo-700 rounded-xl border border-indigo-200">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">2. ตั้งค่าสินทรัพย์ (Fixed Assets & Depreciation)</h2>
                    <p className="text-xs text-slate-500">วิธีคิดค่าเสื่อมราคา, วงเงินสินทรัพย์ และระบบ QR Code ทะเบียนทรัพย์สิน</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าสินทรัพย์')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">วิธีการคำนวณค่าเสื่อมราคามาตรฐาน</label>
                  <select
                    value={assetDepreciationMethod}
                    onChange={(e) => setAssetDepreciationMethod(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="เส้นตรง (Straight Line 5 ปี)">วิธีเส้นตรง (Straight Line - 5 ปี / 20% ต่อปี)</option>
                    <option value="เส้นตรง (Straight Line 10 ปี)">วิธีเส้นตรง (Straight Line - 10 ปี / 10% ต่อปี)</option>
                    <option value="ตัดจ่ายทั้งจำนวน (Expense Immediately)">ตัดจ่ายเป็นค่าใช้จ่ายทันที</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เกณฑ์มูลค่าขึ้นทะเบียนครุภัณฑ์ (บาท)</label>
                  <input
                    type="number"
                    value={assetBudgetLimit}
                    onChange={(e) => setAssetBudgetLimit(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">สินทรัพย์ที่มีมูลค่าเกินเกณฑ์นี้จะถูกออกรหัสทรัพย์สิน</span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <QrCode className="w-5 h-5 text-indigo-600" />
                  <div>
                    <span className="font-bold text-slate-800">สร้าง QR Code ติดทรัพย์สินอัตโนมัติ (Asset Tagging)</span>
                    <p className="text-[11px] text-slate-500">สำหรับให้เจ้าหน้าที่สแกนเช็คสถานะการซ่อมบำรุงผ่านมือถือ</p>
                  </div>
                </div>
                <button
                  onClick={() => setAutoQrAssetTag(!autoQrAssetTag)}
                  className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                    autoQrAssetTag ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {autoQrAssetTag ? 'เปิดใช้งาน' : 'ปิด'}
                </button>
              </div>
            </div>
          )}

          {/* ================= 3. ตั้งค่าข้อมูลโครงการ ================= */}
          {activeSetting === 'project_info' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-50 text-emerald-700 rounded-xl border border-emerald-200">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">3. ตั้งค่าข้อมูลโครงการ (Estate Master Information)</h2>
                    <p className="text-xs text-slate-500">ชื่อนิติบุคคล, เลขทะเบียน, เลขผู้เสียภาษี, แปลงที่ดิน และเบอร์ติดต่อ</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าข้อมูลโครงการ')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ชื่อนิติบุคคลทางการ *</label>
                  <input
                    type="text"
                    value={juristicName}
                    onChange={(e) => setJuristicName(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เลขทะเบียนนิติบุคคลหมู่บ้านจัดสรร</label>
                  <input
                    type="text"
                    value={juristicRegNo}
                    onChange={(e) => setJuristicRegNo(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เลขประจำตัวผู้เสียภาษี (Tax ID)</label>
                  <input
                    type="text"
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">อีเมลติดต่อทางการ</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เบอร์สำนักงานนิติบุคคล</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เบอร์สายด่วน รปภ. / เหตุฉุกเฉิน 24 ชม.</label>
                  <input
                    type="text"
                    value={emergencyPhone}
                    onChange={(e) => setEmergencyPhone(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-rose-600"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">จำนวนแปลง/บ้านทั้งหมดในโครงการ</label>
                  <input
                    type="number"
                    value={totalUnits}
                    onChange={(e) => setTotalUnits(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">พื้นที่ดินรวมทั้งโครงการ (ตารางวา)</label>
                  <input
                    type="number"
                    value={totalLandSqw}
                    onChange={(e) => setTotalLandSqw(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 4. ตั้งค่าเอกสาร ================= */}
          {activeSetting === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-50 text-amber-700 rounded-xl border border-amber-200">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">4. ตั้งค่าเอกสาร (Document Prefix & Templates)</h2>
                    <p className="text-xs text-slate-500">รูปแบบรหัสเอกสารรันนิ่ง, ตรายางนิติบุคคลดิจิทัล และข้อความหัวกระดาษ</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าเอกสาร')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">รูปแบบเลขที่ใบแจ้งหนี้ (Invoice Prefix)</label>
                  <input
                    type="text"
                    value={invoicePrefix}
                    onChange={(e) => setInvoicePrefix(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-blue-700"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">ตัวอย่าง: INV-2026-0001</span>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">รูปแบบเลขที่ใบเสร็จรับเงิน (Receipt Prefix)</label>
                  <input
                    type="text"
                    value={receiptPrefix}
                    onChange={(e) => setReceiptPrefix(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-emerald-700"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">ตัวอย่าง: REC-2026-0001</span>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">รูปแบบเลขที่หนังสือปลอดหนี้</label>
                  <input
                    type="text"
                    value={debtPrefix}
                    onChange={(e) => setDebtPrefix(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1 text-xs">ข้อความหัวเอกสาร / ข้อบังคับที่อ้างอิง</label>
                <input
                  type="text"
                  value={docHeaderNotes}
                  onChange={(e) => setDocHeaderNotes(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs">
                <div>
                  <span className="font-bold text-slate-800">ประทับตรายางดิจิทัลและลายเซ็นอัตโนมัติบนใบเสร็จ (Digital Stamp)</span>
                  <p className="text-[11px] text-slate-500">แสดงตรายางนิติบุคคลสีน้ำเงินเมื่อลูกบ้านดาวน์โหลดใบเสร็จรับเงิน</p>
                </div>
                <button
                  onClick={() => setDigitalStampEnabled(!digitalStampEnabled)}
                  className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                    digitalStampEnabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {digitalStampEnabled ? 'เปิดใช้งาน' : 'ปิด'}
                </button>
              </div>
            </div>
          )}

          {/* ================= 5. ตั้งค่าคณะกรรมการ ================= */}
          {activeSetting === 'board_members' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-purple-50 text-purple-700 rounded-xl border border-purple-200">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">5. ตั้งค่าคณะกรรมการ (Board of Directors Policy)</h2>
                    <p className="text-xs text-slate-500">วาระการดำรงตำแหน่ง, วงเงินอนุมัติงบประมาณ และเกณฑ์องค์ประชุม</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าคณะกรรมการ')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">วาระปัจจุบัน</label>
                  <input
                    type="text"
                    value={boardCurrentTerm}
                    onChange={(e) => setBoardCurrentTerm(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ระยะเวลาวาระ (ปี)</label>
                  <input
                    type="number"
                    value={boardTermYears}
                    onChange={(e) => setBoardTermYears(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">วงเงินอนุมัติจัดซื้อจัดจ้างของคณะกรรมการ (บาท/ครั้ง)</label>
                  <input
                    type="number"
                    value={boardApprovalLimit}
                    onChange={(e) => setBoardApprovalLimit(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-purple-700"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">เกินกว่านี้ต้องขอมติที่ประชุมใหญ่ (AGM)</span>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เกณฑ์องค์ประชุมคณะกรรมการ</label>
                  <input
                    type="text"
                    value={quorumRatio}
                    onChange={(e) => setQuorumRatio(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 6. ตั้งค่าระบบรักษาความปลอดภัยและผู้มาติดต่อ ================= */}
          {activeSetting === 'security_visitors' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-rose-50 text-rose-700 rounded-xl border border-rose-200">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      6. ตั้งค่าระบบรักษาความปลอดภัยและผู้มาติดต่อ (Security & Visitor Gate)
                    </h2>
                    <p className="text-xs text-slate-500">นโยบายแลกบัตรเข้า-ออก, ค่าปรับจอดรถค้างคืน และจุดตรวจ QR Code ลาดตระเวน</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าระบบรักษาความปลอดภัย')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">รูปแบบการลงทะเบียนผู้มาติดต่อ (Visitor Pass)</label>
                  <select
                    value={visitorPassType}
                    onChange={(e) => setVisitorPassType(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="สแกน QR Code + บันทึกบัตรประชาชน">สแกน E-Stamp QR Code + ถ่ายรูปบัตรประชาชน</option>
                    <option value="แลกบัตรแข็งกระดาษแบบดั้งเดิม">แลกบัตรแข็งแบบดั้งเดิม (Visitor Card)</option>
                    <option value="อ่านบัตรประชาชนด้วยเครื่อง Smart Card">อ่านบัตรด้วยเครื่อง Smart Card Reader</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">อัตราค่าปรับจอดรถค้างคืนไม่ได้รับอนุญาต (บาท/คืน)</label>
                  <input
                    type="number"
                    value={overnightParkingFee}
                    onChange={(e) => setOvernightParkingFee(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-rose-600"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">จำนวนจุดตรวจ QR Code ลาดตระเวนรอบโครงการ</label>
                  <input
                    type="number"
                    value={patrolPointsCount}
                    onChange={(e) => setPatrolPointsCount(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ระยะเวลาจอดรถฟรีสำหรับผู้มาติดต่อ (ชั่วโมง)</label>
                  <input
                    type="number"
                    value={visitorMaxStayHours}
                    onChange={(e) => setVisitorMaxStayHours(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <Car className="w-5 h-5 text-rose-600" />
                  <div>
                    <span className="font-bold text-slate-800">ระบบกล้องอ่านป้ายทะเบียน LPR เปิดไม้กั้นอัตโนมัติ</span>
                    <p className="text-[11px] text-slate-500">สำหรับลูกบ้านที่ลงทะเบียนป้ายทะเบียนรถในสารบบนิติบุคคล</p>
                  </div>
                </div>
                <button
                  onClick={() => setLprBarrierAutoOpen(!lprBarrierAutoOpen)}
                  className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                    lprBarrierAutoOpen ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {lprBarrierAutoOpen ? 'เปิดใช้งาน' : 'ปิด'}
                </button>
              </div>
            </div>
          )}

          {/* ================= 7. ตั้งค่าสำหรับผู้พัฒนาโครงการ ================= */}
          {activeSetting === 'developer' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-teal-50 text-teal-700 rounded-xl border border-teal-200">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">7. ตั้งค่าสำหรับผู้พัฒนาโครงการ (Developer Portal Config)</h2>
                    <p className="text-xs text-slate-500">ข้อมูลผู้พัฒนา, ระยะเวลาประกันส่วนกลาง และกองทุนแรกเริ่ม (Sinking Fund)</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าสำหรับผู้พัฒนาโครงการ')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ชื่อบริษัทผู้พัฒนาโครงการ</label>
                  <input
                    type="text"
                    value={developerName}
                    onChange={(e) => setDeveloperName(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เบอร์ติดต่อฝ่ายบริการหลังการขาย Developer</label>
                  <input
                    type="text"
                    value={developerContact}
                    onChange={(e) => setDeveloperContact(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ระยะเวลารับประกันงานโครงสร้างส่วนกลาง (ปี)</label>
                  <input
                    type="number"
                    value={warrantyYears}
                    onChange={(e) => setWarrantyYears(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ยอดเงินกองทุนแรกเริ่มส่งมอบ (Sinking Fund)</label>
                  <input
                    type="number"
                    value={sinkingFundAmount}
                    onChange={(e) => setSinkingFundAmount(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-teal-700"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 8. ตั้งค่าการมองเห็นเอกสารในฝั่งบัญชีแยกตามสถานะ (แอปปลูกบ้าน) ================= */}
          {activeSetting === 'resident_doc_visibility' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-blue-50 text-blue-700 rounded-xl border border-blue-200">
                    <Eye className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      8. ตั้งค่าการมองเห็นเอกสารในฝั่งบัญชีแยกตามสถานะ (แอปปลูกบ้าน)
                    </h2>
                    <p className="text-xs text-slate-500">ควบคุมสิทธิการเข้าถึงรายงานการเงิน, รายงานการประชุม และการคุ้มครองข้อมูล PDPA</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าการมองเห็นเอกสารแอปปลูกบ้าน')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">อนุญาตให้ลูกบ้านดูรายงานสรุปงบการเงินประจำเดือน</span>
                    <p className="text-[11px] text-slate-500">แสดงงบรายรับ-รายจ่ายของนิติบุคคลในแอปพลิเคชัน</p>
                  </div>
                  <button
                    onClick={() => setAllowResidentViewFinancialReport(!allowResidentViewFinancialReport)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      allowResidentViewFinancialReport ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {allowResidentViewFinancialReport ? 'เปิด' : 'ปิด'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">อนุญาตให้ดูรายงานการประชุมใหญ่สามัญประจำปี (AGM Minutes)</span>
                    <p className="text-[11px] text-slate-500">เปิดเอกสารสรุปมติที่ประชุมให้ลูกบ้านดาวน์โหลด</p>
                  </div>
                  <button
                    onClick={() => setAllowResidentViewAgmMinutes(!allowResidentViewAgmMinutes)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      allowResidentViewAgmMinutes ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {allowResidentViewAgmMinutes ? 'เปิด' : 'ปิด'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">ระบบปกปิดข้อมูลส่วนบุคคลตามกฎหมาย PDPA (Masking)</span>
                    <p className="text-[11px] text-slate-500">ซ่อนเลขบัตรประชาชนและเบอร์โทรศัพท์ของลูกบ้านท่านอื่นในรายงานรวม</p>
                  </div>
                  <button
                    onClick={() => setPdpaMaskingEnabled(!pdpaMaskingEnabled)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      pdpaMaskingEnabled ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {pdpaMaskingEnabled ? 'เปิดคุ้มครอง PDPA' : 'ปิด'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="font-semibold text-slate-700 block mb-1">ระยะเวลาแสดงประวัติใบเสร็จย้อนหลังในแอป</label>
                    <select
                      value={residentReceiptHistoryMonths}
                      onChange={(e) => setResidentReceiptHistoryMonths(Number(e.target.value))}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                    >
                      <option value={6}>ย้อนหลัง 6 เดือน</option>
                      <option value={12}>ย้อนหลัง 12 เดือน (1 ปี)</option>
                      <option value={24}>ย้อนหลัง 24 เดือน (2 ปี)</option>
                      <option value={0}>แสดงทั้งหมดตลอดชีพ</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 9. ตั้งค่าปรับแต่งเมนู ================= */}
          {activeSetting === 'menu_customization' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-pink-50 text-pink-700 rounded-xl border border-pink-200">
                    <Sliders className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">9. ตั้งค่าปรับแต่งเมนู (Menu & Theme Customization)</h2>
                    <p className="text-xs text-slate-500">เปิด-ปิด โมดูลบริการ และปรับแต่งธีมสีโครงการ</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าปรับแต่งเมนู')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <span className="font-semibold text-slate-800">เมนูโทรด่วน Hotline & สายตรวจ 24 ชม.</span>
                  <button
                    onClick={() => setShowHotlineMenu(!showHotlineMenu)}
                    className={`px-3 py-1 rounded-lg font-semibold ${
                      showHotlineMenu ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {showHotlineMenu ? 'แสดง' : 'ซ่อน'}
                  </button>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <span className="font-semibold text-slate-800">เมนูจองสิ่งอำนวยความสะดวก (สโมสร / สระว่ายน้ำ)</span>
                  <button
                    onClick={() => setShowFacilityBooking(!showFacilityBooking)}
                    className={`px-3 py-1 rounded-lg font-semibold ${
                      showFacilityBooking ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {showFacilityBooking ? 'แสดง' : 'ซ่อน'}
                  </button>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <span className="font-semibold text-slate-800">เมนูลงมติและโหวตออนไลน์ E-Voting (AGM)</span>
                  <button
                    onClick={() => setShowEVotingMenu(!showEVotingMenu)}
                    className={`px-3 py-1 rounded-lg font-semibold ${
                      showEVotingMenu ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {showEVotingMenu ? 'แสดง' : 'ซ่อน'}
                  </button>
                </div>

                <div className="pt-2">
                  <label className="font-semibold text-slate-700 block mb-1">ธีมสีหลักของแอปพลิเคชัน</label>
                  <select
                    value={primaryThemeColor}
                    onChange={(e) => setPrimaryThemeColor(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="น้ำเงินสุขุม (Classic Navy Blue)">น้ำเงินสุขุม (Classic Navy Blue)</option>
                    <option value="เขียวมรกต (Emerald Forest)">เขียวมรกต (Emerald Forest)</option>
                    <option value="ม่วงหรูหรา (Royal Purple)">ม่วงหรูหรา (Royal Purple)</option>
                    <option value="เทาหินอ่อน (Slate Minimalist)">เทาหินอ่อน (Slate Minimalist)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ================= 10. ตั้งค่ากรรมสิทธิ์ชาวต่างชาติ ================= */}
          {activeSetting === 'foreign_quota' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-cyan-50 text-cyan-700 rounded-xl border border-cyan-200">
                    <Globe2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      10. ตั้งค่ากรรมสิทธิ์ชาวต่างชาติ (Foreign Ownership Quota)
                    </h2>
                    <p className="text-xs text-slate-500">สัดส่วนโควตาไม่เกิน 49%, เอกสารแบบฟอร์ม FET และระบบ 2 ภาษา</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่ากรรมสิทธิ์ชาวต่างชาติ')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เพดานโควตากรรมสิทธิ์ชาวต่างชาติสูงสุด (%)</label>
                  <input
                    type="number"
                    value={foreignQuotaMaxPercent}
                    onChange={(e) => setForeignQuotaMaxPercent(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-cyan-700"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">ตาม พ.ร.บ. อาคารชุด/จัดสรร ไม่เกิน 49%</span>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">สัดส่วนการถือครองปัจจุบัน</label>
                  <input
                    type="number"
                    step="0.01"
                    disabled
                    value={foreignCurrentPercent}
                    className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-xl font-mono font-bold text-slate-700"
                  />
                  <span className="text-[10px] text-emerald-600 font-medium mt-1 block">
                    อยู่ในเกณฑ์ปลอดภัย (โควตาคงเหลือ {(foreignQuotaMaxPercent - foreignCurrentPercent).toFixed(2)}%)
                  </span>
                </div>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">บังคับแนบเอกสารการโอนเงินจากต่างประเทศ (FET Form)</span>
                    <p className="text-[11px] text-slate-500">เพื่อใช้ยื่นจดทะเบียนโอนกรรมสิทธิ์ ณ สำนักงานที่ดิน</p>
                  </div>
                  <button
                    onClick={() => setRequireFetForm(!requireFetForm)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      requireFetForm ? 'bg-cyan-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {requireFetForm ? 'บังคับแนบ' : 'ไม่บังคับ'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">เปิดใช้งานเอกสารและสัญญา 2 ภาษา (ไทย - อังกฤษ)</span>
                    <p className="text-[11px] text-slate-500">สำหรับใบแจ้งหนี้และหนังสือสำคัญปลอดหนี้</p>
                  </div>
                  <button
                    onClick={() => setBilingualDocs(!bilingualDocs)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      bilingualDocs ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {bilingualDocs ? 'เปิดใช้งาน' : 'ปิด'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================= 11. ตั้งค่าสิทธิ์ผู้ใช้งาน ================= */}
          {activeSetting === 'user_permissions' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-50 text-emerald-700 rounded-xl border border-emerald-200">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">11. ตั้งค่าสิทธิ์ผู้ใช้งาน (Role-Based Access Control)</h2>
                    <p className="text-xs text-slate-500">จัดการสิทธิ์เข้าถึงของนิติบุคคล, เจ้าหน้าที่บัญชี, รปภ., และลูกบ้าน</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าสิทธิ์ผู้ใช้งาน')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ระยะเวลาตัดเซสชันเมื่อไม่มีการใช้งาน (นาที)</label>
                  <input
                    type="number"
                    value={sessionTimeoutMins}
                    onChange={(e) => setSessionTimeoutMins(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เก็บบันทึกประวัติการแก้ไขข้อมูล (Audit Log Days)</label>
                  <input
                    type="number"
                    value={auditLogRetentionDays}
                    onChange={(e) => setAuditLogRetentionDays(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-emerald-600" />
                  <div>
                    <span className="font-bold text-slate-800">ยืนยันตัวตนสองชั้น (2-Factor Authentication) สำหรับ Admin</span>
                    <p className="text-[11px] text-slate-500">ส่งรหัส OTP ผ่าน SMS หรือ Email ก่อนเข้าแก้ไขข้อมูลการเงิน</p>
                  </div>
                </div>
                <button
                  onClick={() => setTwoFactorAuth(!twoFactorAuth)}
                  className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                    twoFactorAuth ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {twoFactorAuth ? 'เปิดใช้งาน' : 'ปิด'}
                </button>
              </div>
            </div>
          )}

          {/* ================= 12. ตั้งค่าการตอบกลับข้อความ ================= */}
          {activeSetting === 'auto_reply' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-sky-50 text-sky-700 rounded-xl border border-sky-200">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      12. ตั้งค่าการตอบกลับข้อความ (Automated Auto-Replies)
                    </h2>
                    <p className="text-xs text-slate-500">ข้อความตอบกลับอัตโนมัตินอกเวลาทำการ และระบบแจ้งเตือน LINE Notify</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าการตอบกลับข้อความ')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">เวลาทำการของสำนักงานนิติบุคคล</label>
                  <input
                    type="text"
                    value={officeHours}
                    onChange={(e) => setOfficeHours(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ข้อความตอบกลับอัตโนมัติ (Auto-Reply Message)</label>
                  <textarea
                    rows={3}
                    value={autoReplyMessage}
                    onChange={(e) => setAutoReplyMessage(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">ส่งข้อความแจ้งเตือนผ่าน LINE Official Account / Bot</span>
                    <p className="text-[11px] text-slate-500">แจ้งเตือนแอดมินทันทีเมื่อมีลูกบ้านส่งข้อร้องเรียนหรือแจ้งซ่อมด่วน</p>
                  </div>
                  <button
                    onClick={() => setLineNotifyActive(!lineNotifyActive)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      lineNotifyActive ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {lineNotifyActive ? 'เชื่อมต่อแล้ว' : 'ปิด'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================= 13. ตั้งค่าพัสดุ ================= */}
          {activeSetting === 'parcels' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-indigo-50 text-indigo-700 rounded-xl border border-indigo-200">
                    <Package className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">13. ตั้งค่าพัสดุ (Parcel Station & Shelf Management)</h2>
                    <p className="text-xs text-slate-500">จัดการโซนชั้นวางพัสดุ, ระยะเวลารับพัสดุ และการแจ้งเตือนลูกบ้าน</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าพัสดุ')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">โซนชั้นวางพัสดุของโครงการ</label>
                  <input
                    type="text"
                    value={parcelShelfZones}
                    onChange={(e) => setParcelShelfZones(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ระยะเวลาจัดเก็บพัสดุสูงสุดก่อนแจ้งเตือนซ้ำ (วัน)</label>
                  <input
                    type="number"
                    value={parcelMaxKeepDays}
                    onChange={(e) => setParcelMaxKeepDays(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">ส่ง Notification แจ้งเตือนลูกบ้านทันทีเมื่อสแกนพัสดุเข้า</span>
                    <p className="text-[11px] text-slate-500">พร้อมแนบรูปภาพพัสดุและรหัสชั้นวาง</p>
                  </div>
                  <button
                    onClick={() => setParcelSmsAlert(!parcelSmsAlert)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      parcelSmsAlert ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {parcelSmsAlert ? 'เปิดใช้งาน' : 'ปิด'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">บังคับลงลายมือชื่อดิจิทัลก่อนรับพัสดุ (Digital Signature)</span>
                    <p className="text-[11px] text-slate-500">เพื่อใช้เป็นหลักฐานยืนยันการรับมอบพัสดุ</p>
                  </div>
                  <button
                    onClick={() => setParcelRequireSign(!parcelRequireSign)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      parcelRequireSign ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {parcelRequireSign ? 'เปิดใช้งาน' : 'ปิด'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================= 14. ตั้งค่าแชท ================= */}
          {activeSetting === 'chat_settings' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-violet-50 text-violet-700 rounded-xl border border-violet-200">
                    <MessagesSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">14. ตั้งค่าแชท (Live Chat & FAQ Bot)</h2>
                    <p className="text-xs text-slate-500">ระบบสนทนาระหว่างลูกบ้านและนิติบุคคล, บอทตอบคำถามอัตโนมัติ</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าแชท')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">เปิดระบบแชทสดสนทนากับนิติบุคคล (Live Chat)</span>
                    <p className="text-[11px] text-slate-500">ลูกบ้านสามารถส่งข้อความสอบถามหรือแจ้งเรื่องได้โดยตรง</p>
                  </div>
                  <button
                    onClick={() => setChatEnabled(!chatEnabled)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      chatEnabled ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {chatEnabled ? 'เปิด' : 'ปิด'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">บอทช่วยตอบคำถามที่พบบ่อยอัตโนมัติ (Smart FAQ Bot)</span>
                    <p className="text-[11px] text-slate-500">ช่วยตอบเรื่องระเบียบการชำระเงิน, เวลาเปิดสโมสร, และเบอร์ติดต่อฉุกเฉิน</p>
                  </div>
                  <button
                    onClick={() => setChatBotFaqEnabled(!chatBotFaqEnabled)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      chatBotFaqEnabled ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {chatBotFaqEnabled ? 'เปิดใช้งาน' : 'ปิด'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">อนุญาตให้ส่งรูปภาพและไฟล์เอกสารแนบในแชท</span>
                    <p className="text-[11px] text-slate-500">ขนาดไฟล์ไม่เกิน 10MB ต่อรูป</p>
                  </div>
                  <button
                    onClick={() => setChatMediaAllow(!chatMediaAllow)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      chatMediaAllow ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {chatMediaAllow ? 'อนุญาต' : 'ปิด'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================= 15. ตั้งค่ารูปแบบใบแจ้งหนี้ ================= */}
          {activeSetting === 'invoice_template' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-50 text-amber-700 rounded-xl border border-amber-200">
                    <Receipt className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      15. ตั้งค่ารูปแบบใบแจ้งหนี้ (Invoice & Receipt Design)
                    </h2>
                    <p className="text-xs text-slate-500">รูปแบบฟอร์ม A4, QR Payment PromptPay และข้อความคำเตือนท้ายบิล</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่ารูปแบบใบแจ้งหนี้')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">สไตล์แบบฟอร์มเอกสาร</label>
                  <select
                    value={invoiceTemplateStyle}
                    onChange={(e) => setInvoiceTemplateStyle(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="แบบทางการ (Official A4 with PromptPay QR)">
                      แบบทางการ (Official A4 + PromptPay QR Code)
                    </option>
                    <option value="แบบสลิปความร้อน (Thermal 80mm Slip)">แบบสลิปความร้อน (Thermal 80mm Slip)</option>
                    <option value="แบบมินิมอล (Modern Clean Minimal)">แบบมินิมอล (Modern Clean Minimal)</option>
                  </select>
                </div>

                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeWatermark}
                      onChange={(e) => setIncludeWatermark(e.target.checked)}
                      className="rounded text-blue-600"
                    />
                    <span className="font-medium text-slate-700">แสดงลายน้ำตรานิติบุคคลโปร่งใสบนเอกสาร</span>
                  </label>
                </div>
              </div>

              <div className="text-xs">
                <label className="font-semibold text-slate-700 block mb-1">ข้อความระเบียบการชำระเงินท้ายใบแจ้งหนี้</label>
                <textarea
                  rows={3}
                  value={invoiceFooterNote}
                  onChange={(e) => setInvoiceFooterNote(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* ================= 16. ตั้งค่าเมนูแอดมิน ================= */}
          {activeSetting === 'admin_menu' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-slate-100 text-slate-800 rounded-xl border border-slate-300">
                    <LayoutGrid className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">16. ตั้งค่าเมนูแอดมิน (Admin Sidebar & Dashboard UI)</h2>
                    <p className="text-xs text-slate-500">การแสดงผลแถบเมนู 16 หมวดหมู่, ทางลัดคีย์บอร์ด และวิดเจ็ตสรุปผล</p>
                  </div>
                </div>
                <button
                  onClick={() => triggerSave('ตั้งค่าเมนูแอดมิน')}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" /> บันทึก
                </button>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">โหมดแสดงแถบเมนูด้านซ้ายแบบย่อไอคอนเริ่มต้น (Icon-Only Mode)</span>
                    <p className="text-[11px] text-slate-500">เพิ่มพื้นที่การทำงานสำหรับหน้าจอขนาดกะทัดรัด</p>
                  </div>
                  <button
                    onClick={() => setCompactSidebarByDefault(!compactSidebarByDefault)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      compactSidebarByDefault ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {compactSidebarByDefault ? 'เปิด' : 'ปิด'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">เปิดใช้งานปุ่มลัดคีย์บอร์ด (Keyboard Shortcuts)</span>
                    <p className="text-[11px] text-slate-500">กด Ctrl+K เพื่อค้นหาเมนูด่วน หรือ Tab เพื่อเปลี่ยนหมวดงาน</p>
                  </div>
                  <button
                    onClick={() => setEnableKeyboardShortcuts(!enableKeyboardShortcuts)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      enableKeyboardShortcuts ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {enableKeyboardShortcuts ? 'เปิดใช้งาน' : 'ปิด'}
                  </button>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-800">แสดงการ์ดสรุป KPI ด้านบนหน้าภาพรวม (Quick Stat Widgets)</span>
                    <p className="text-[11px] text-slate-500">แสดงยอดสลิปรอตรวจ, พัสดุคงค้าง และสถานะจุดตรวจ รปภ.</p>
                  </div>
                  <button
                    onClick={() => setShowQuickStatWidgets(!showQuickStatWidgets)}
                    className={`p-1.5 rounded-lg text-xs font-semibold px-3 transition-colors ${
                      showQuickStatWidgets ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {showQuickStatWidgets ? 'เปิด' : 'ปิด'}
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Supabase Connection Bar at Bottom */}
      <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800">
            <Database className="w-4 h-4" />
          </div>
          <div>
            <span className="font-bold text-slate-800">การเชื่อมต่อฐานข้อมูลคลาวด์ (Supabase Cloud Database)</span>
            <p className="text-slate-500 text-[11px]">
              {isSupabaseConnected ? 'เชื่อมต่อ Supabase Database พร้อมใช้งาน' : 'ทำงานในโหมด Local Storage ออฟไลน์'}
            </p>
          </div>
        </div>
        <button
          onClick={onOpenSupabaseModal}
          className="px-3.5 py-1.5 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 rounded-xl text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
        >
          ตั้งค่าการเชื่อมต่อฐานข้อมูล
        </button>
      </div>
    </div>
  );
};
